import { Button, Checkbox, Form, Input, Radio, Spin } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { SspBean, SspListItemBean } from 'common/api/api.ts';
import { AnchorsForm } from 'common/components/AnchorsForm.tsx';
import { ButtonsPanel } from 'common/components/ButtonsPanel.tsx';
import { CopyToClipboardButton } from 'common/components/CopyToClipboardButton.tsx';
import { FormatTypeSelector } from 'common/components/dictionary/FormatTypeSelector.tsx';
import { SspTypeSelector } from 'common/components/dictionary/SspTypeSelector.tsx';
import { FormCard } from 'common/components/FormCard.tsx';
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx';
import { StatusSwitch } from 'common/components/StatusSwitch.tsx';
import { useAppContext } from 'common/context/AppContext.tsx';
import { CommonRoutes } from 'common/context/commonRoutes.ts';
import sspApi from 'common/pages/ssp/SspApi.ts';
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import { apiCall, FormMode, getCopyName, normalizeArrayFields } from 'common/util/Util.tsx';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';





import SspTypeEnum = SspListItemBean.SspTypeEnum
import TrackingTypeEnum = SspBean.TrackingTypeEnum


const { Group: RadioGroup } = Radio

interface SspFormProps {
    mode: 'create' | 'edit' | 'copy'
    admin: boolean
}

const SspForm: React.FC<SspFormProps> = ({ mode, admin }: SspFormProps) => {
    const navigateTo = useNavigate()
    const [loading, setLoading] = useState(false)
    const { id } = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0
    const appContext = useAppContext()
    const [form] = Form.useForm()
    const [entity, setEntity] = useState<SspBean>({} as SspBean)

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            if (values.outerSspList) {
                normalizeArrayFields(['outerSspList'], form)
            }

            const newEntity = { ...entity, ...values }

            apiCall(
                setLoading,
                appContext,
                () => {
                    if (mode === 'edit' && id) {
                        return sspApi.edit(idNum, newEntity)
                    } else {
                        return sspApi.create(newEntity)
                    }
                },
                (response) => {
                    queueListRepositionOnModifiedChange({
                        appContext,
                        routeKey: CommonRoutes.SSP,
                        previousEntity: entity,
                        responseEntity: response?.data,
                        enabled: mode === 'edit'
                    })

                    navigateTo(CommonRoutes.SSP)
                }
            )
        })
    }

    const handleTokenGenerator = async () => {
        apiCall(
            setLoading,
            appContext,
            () => sspApi.generateToken(idNum),
            (res: any) => {
                setEntity({ ...entity, outerSspToken: res.token })
                form.setFieldsValue({ ...form.getFieldsValue(), outerSspToken: res.token })
            }
        )
    }

    const loadData = () => {
        if (mode === 'edit' || mode === 'copy') {
            apiCall(
                setLoading,
                appContext,
                () => {
                    return sspApi.view(idNum)
                },
                (res) => {
                    if (res && res.data) {
                        const entityData = mode === 'copy' ? { ...res.data, name: getCopyName(res.data.name) } : res.data
                        setEntity(entityData)
                        form.setFieldsValue({ ...entityData })
                    }
                }
            )
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    function onCancel() {
        navigateTo(CommonRoutes.SSP)
    }

    function onDelete() {
        apiCall(setLoading, appContext, () => sspApi.delete(idNum), () => navigateTo(CommonRoutes.SSP))
    }

    const sspReportUrl = mode === 'edit' ? sspApi.generateApiReportLink(entity.outerSspToken) : undefined

    function winOnImpressionChange(val) {
        const winOnImpression = val?.target?.checked
        setEntity({ ...entity, winOnImpression: winOnImpression })
        if (form.getFieldValue('trackingType') === TrackingTypeEnum.Win) {
          winOnImpression && form.setFieldValue('trackingType', undefined)
        }
    }

  return (
        <>
            <Spin spinning={loading}>
                <AnchorsForm form={form} onFinish={handleSubmit}>
                    <FormCard
                        id="general"
                        title="General"
                        extra={<StatusSwitch entity={entity} setEntity={setEntity}/>}
                        styles={{header: {borderBottom: 'none'}}}
                    >

                        <FormItem
                          name="agencyId"
                          label="Agency"
                          hidden={!admin}
                        >
                          <AgencySelector disabled={mode == FormMode.edit} placeholder="All agencies" onChange={(agencyId: number) => setEntity((prev) => ({ ...prev, agencyId }))} />
                        </FormItem>
                        <FormItem
                            required
                            name="name"
                            rules={[{ required: true, message: 'Please input SSP name' }]} label="Name"
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            name="internalName"
                            label="Internal Name"
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            required
                            name="endpoint"
                            rules={[{ required: true, message: 'Please input SSP endpoint' }]} label="Endpoint"
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            required
                            name="sspType"
                            label="SSP Type"
                            initialValue={SspTypeEnum.OpenRTB}
                            rules={[{ required: true, message: 'Please select SSP type' }]}
                        >
                            <SspTypeSelector
                              onChange={sspType => {
                                if (form.getFieldValue('trackingType') === TrackingTypeEnum.Win && sspType === SspTypeEnum.CommonXML) {
                                  form.setFieldValue('trackingType', undefined)
                                }
                                setEntity({...entity, sspType})
                              }}
                            />
                        </FormItem>
                        <FormItem name="creativeType" label="Specific Format Type">
                          <FormatTypeSelector placeholder="Any Format Types" />
                        </FormItem>
                    </FormCard>
                    <FormCard
                        id="settings"
                        title="Settings"
                    >
                        <FormItem name="winOnImpression" valuePropName="checked" initialValue={false} label="Count Win on Impression">
                            <Checkbox onChange={winOnImpressionChange} />
                        </FormItem>
                        <FormItem name="vastWinOnly" valuePropName="checked" initialValue={false} label="VAST Win only">
                            <Checkbox />
                        </FormItem>
                        <FormItem name="auctionMode" rules={[{ required: true }]} label="DSP Auction type"
                                  initialValue={SspBean.AuctionModeEnum.AuctionTypeFromRequest}
                        >
                            <RadioGroup>
                                {Object.values([
                                    SspBean.AuctionModeEnum.AuctionTypeFromRequest,
                                    SspBean.AuctionModeEnum.FirstPrice,
                                    SspBean.AuctionModeEnum.SecondPrice
                                ]).map((mode) => (
                                    <Radio key={mode} value={mode}>
                                        {mode}
                                    </Radio>
                                ))}
                            </RadioGroup>
                        </FormItem>
                        <FormItem name="trackingType" rules={[{ required: true }]} label="Tracking type"
                                  initialValue={SspBean.TrackingTypeEnum.View}
                        >
                            <RadioGroup>
                                {Object.values(SspBean.TrackingTypeEnum)
                                  .filter(trackingType => (entity.sspType !== SspTypeEnum.CommonXML && !entity.winOnImpression) || trackingType !== SspBean.TrackingTypeEnum.Win)
                                  .map((mode) => (
                                    <Radio key={mode} value={mode}>
                                        {mode}
                                    </Radio>
                                ))}
                            </RadioGroup>
                        </FormItem>
                        {mode === 'edit' && (
                            <>
                                <FormItem label="API Stats Token">
                                    <Input
                                        value={entity.outerSspToken}
                                        addonAfter={
                                            <Button
                                                style={{
                                                    border: 0,
                                                    backgroundColor: '#fafafa'
                                                }}
                                                size="small"
                                                onClick={handleTokenGenerator}
                                            >
                                                Generate
                                            </Button>
                                        }
                                    />
                                </FormItem>
                                <FormItem label="API Stats Link">
                                    <CopyToClipboardButton text={sspReportUrl || ''} />
                                </FormItem>
                            </>
                        )}
                        {/*<FormItem*/}
                        {/*    name="allowedAdvertiserIds"*/}
                        {/*    label="Allowed Advetisers"*/}
                        {/*>*/}
                        {/*    <AdvertiserSelector />*/}
                        {/*</FormItem>*/}
                    </FormCard>
                    <FormCard
                        id="advancedSettings"
                        title="Advanced Ad Exchange settings"
                    >
                        <FormItem
                          name="bidswitchSspListMode"
                          rules={[{ required: true }]}
                          label="SSP List Mode"
                          initialValue={SspBean.BidswitchSspListModeEnum.Black}
                        >
                            <RadioGroup>
                                {Object.values(SspBean.BidswitchSspListModeEnum).map((mode) => (
                                    <Radio key={mode} value={mode}>
                                        {mode}
                                    </Radio>
                                ))}
                            </RadioGroup>
                        </FormItem>
                        <FormItem
                            name="bidswitchSspList"
                            rules={[
                                {
                                    message: 'Please enter SSP List'
                                }
                            ]}
                            label="SSP List"
                        >
                            <Input />
                        </FormItem>
                    </FormCard>
                    <ButtonsPanel onCancel={onCancel}
                                  showDelete={mode === 'edit'} onDelete={onDelete}
                    ></ButtonsPanel>
                </AnchorsForm>
            </Spin>
        </>
    )
}

export default SspForm
